import "./SchedulePlanner.css";
import "./EmployeeTrainingIdentified.css";
import { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDistinctFilters } from "../Components/Menu/Branch";
import FuncAreaViewer from "../Components/Viewers/FuncAreaViewer";
import TrngCourseViewer from "../Components/Viewers/TrngCourseViewer";
import FacultyViewer from "../Components/Viewers/FacultyViewer";
import BatchViewer from "../Components/Viewers/BatchViewer";
import { batchStatusAPI, emailAPI, facultyAPI } from "./api";
import { getFuncAreas, getTrngCourses, invalidateTrngCourses } from "./cache";
import DataTable from "./DataTable";
import UpdateComponent from "../Components/UpdateComponent/UpdateComponent";

const MONTH_MAP = {
  April: "04", May: "05", June: "06", July: "07",
  August: "08", September: "09", October: "10", November: "11",
  December: "12", January: "01", February: "02", March: "03"
};

const INIT_FORM = {
  batch_type: "DEFA", branch: "", batch_sr_no: "",
  func_area_code: "", func_sub_area_code: "", func_area_display: "",
  trng_course_code: "", trng_course_display: "",
  title: "", start_date: null, end_date: null, start_time: "", end_time: "",
  no_of_hrs: "", batch_size: "",
  facultyList: [],
  professional_fees: "", trng_rel_cost: "", trng_rel_cost_rmk: "",
  venue: "", trng_location: "", trng_city: "",
  division: "", trng_status: "", emp_cost: "", total_cost: "",
  per_emp_per_day_fc: "", per_emp_per_day_mc: "", per_emp_per_day_cost: "",
  reason: "", dph_sal_code: "", trng_type: "", number_attended: "",
};

export default function SchedulePlanner() {
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastKeyRef = useRef(null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const activeParamsRef = useRef({});
  const [showInsertForm, setShowInsertForm] = useState(false);
  const [form, setForm] = useState(INIT_FORM);
  const [showFuncAreaViewer, setShowFuncAreaViewer] = useState(false);
  const [showTrngCourseViewer, setShowTrngCourseViewer] = useState(false);
  const [showFacultyViewer, setShowFacultyViewer] = useState(false);
  const [showBatchViewer, setShowBatchViewer] = useState(false);
  const [branches, setBranches] = useState([]);
  const [funcAreaMap, setFuncAreaMap] = useState({});
  const [trngCourseMap, setTrngCourseMap] = useState({});
  const [facultyMap, setFacultyMap] = useState({});
  const [showEditCols, setShowEditCols] = useState(false);
  const [showDeleteCols, setShowDeleteCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);

  useEffect(() => {
    getDistinctFilters().then(({ branches: b }) => setBranches(b));
  }, []);

  const buildParams = () => {
    const params = {};
    if (selectedBranch) {
      params.branch = selectedBranch;
    } else {
      params.limit = 25;
    }
    // Financial year e.g. "2024-25" means April 2024 – March 2025
    if (selectedYear && selectedMonth) {
      // Determine the calendar year for the selected month within the financial year
      const fyStartYear = Number(selectedYear.split('-')[0]);
      const calYear = ['January', 'February', 'March'].includes(selectedMonth)
        ? fyStartYear + 1
        : fyStartYear;
      const mm = MONTH_MAP[selectedMonth];
      const daysInMonth = new Date(calYear, Number(mm), 0).getDate();
      params.start_date = `${calYear}-${mm}-01`;
      params.end_date   = `${calYear}-${mm}-${String(daysInMonth).padStart(2, '0')}`;
    } else if (selectedYear) {
      // Full financial year: April 1 of start year → March 31 of end year
      const fyStartYear = Number(selectedYear.split('-')[0]);
      params.start_date = `${fyStartYear}-04-01`;
      params.end_date   = `${fyStartYear + 1}-03-31`;
    }
    return params;
  };

  const handleView = async () => {
    setTableLoading(true);
    setShowTable(false);
    setTableRows([]);
    lastKeyRef.current = null;
    setHasMore(false);
    setShowEditCols(false);
    setShowDeleteCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setUpdateMsg(null);
    setDeleteMsg(null);
    const params = buildParams();
    activeParamsRef.current = params;

    try {
      invalidateTrngCourses();
      const [funcAreas, courses, faculties] = await Promise.all([
        getFuncAreas(),
        getTrngCourses(),
        facultyAPI.getAll(),
      ]);

      const fMap = {};
      (Array.isArray(funcAreas) ? funcAreas : []).forEach(f => {
        fMap[String(f.func_sub_area_code)] = f.func_sub_area_name;
      });
      setFuncAreaMap(fMap);

      const tMap = {};
      (Array.isArray(courses) ? courses : []).forEach(c => {
        tMap[String(c.trng_course_code)] = c.trng_course_name;
      });
      setTrngCourseMap(tMap);

      const facMap = {};
      (Array.isArray(faculties) ? faculties : []).forEach(f => {
        facMap[String(f.faculty_code)] = f.faculty_name;
      });
      setFacultyMap(facMap);

      // Always use single-page load with Load More for all filter combinations
      const res = await batchStatusAPI.getPage(params, null);
      setTableRows(Array.isArray(res.items) ? res.items : []);
      lastKeyRef.current = res.lastKey ?? null;
      setHasMore(res.hasMore ?? !!res.lastKey);

      setShowTable(true);
    } catch (e) {
      setTableRows([]);
      lastKeyRef.current = null;
      setHasMore(false);
      setShowTable(true);
      alert('Failed to load schedule data.');
    } finally {
      setTableLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!lastKeyRef.current || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await batchStatusAPI.getPage(activeParamsRef.current, lastKeyRef.current);
      const newItems = Array.isArray(res.items) ? res.items : [];
      setTableRows((prev) => [...prev, ...newItems]);
      lastKeyRef.current = res.lastKey ?? null;
      setHasMore(res.hasMore ?? !!res.lastKey);
    } catch (e) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCopyRow = (row) => {
    const batchType = row.func_area_code === 'DEFA' ? 'DEFA' : 'FREE';
    const parseDate = (d) => { if (!d) return null; const p = new Date(d); return isNaN(p) ? null : p; };
    setForm({
      ...INIT_FORM,
      batch_type: batchType,
      branch: row.branch ?? "",
      batch_sr_no: "",
      func_area_code: row.func_area_code ?? "",
      func_sub_area_code: row.func_sub_area_code ?? "",
      func_area_display: funcAreaMap[String(row.func_sub_area_code)] ?? row.func_sub_area_code ?? "",
      trng_course_code: row.trng_course_code ?? "",
      trng_course_display: trngCourseMap[String(row.trng_course_code)] ?? row.trng_course_code ?? "",
      title: row.title ?? "",
      start_date: null,
      end_date: null,
      start_time: row.start_time ?? "",
      end_time: row.end_time ?? "",
      no_of_hrs: row.no_of_hrs ? String(row.no_of_hrs) : "",
      batch_size: row.batch_size ? String(row.batch_size) : "",
      professional_fees: row.professional_fees ?? "",
      trng_rel_cost: row.trng_rel_cost ?? "",
      trng_rel_cost_rmk: row.trng_rel_cost_rmk ?? "",
      venue: row.venue ?? "",
      trng_location: row.trng_location ?? "",
      trng_city: row.trng_city ?? "",
      division: row.division ?? "",
      emp_cost: row.emp_cost ?? "",
      total_cost: row.total_cost ?? "",
      per_emp_per_day_fc: row.per_emp_per_day_fc ?? "",
      per_emp_per_day_mc: row.per_emp_per_day_mc ?? "",
      per_emp_per_day_cost: row.per_emp_per_day_cost ?? "",
      reason: row.reason ?? "",
      dph_sal_code: row.dph_sal_code ?? "",
      number_attended: row.number_attended ?? "",
    });
    setShowTable(false);
    setShowInsertForm(true);
  };

  const scheduleColumns = [
    { header: "Batch Sr No", accessor: (row) => row.batch_sr_no ?? "" },
    { header: "Batch Type", accessor: (row) => {
      const bt = row.batch_type || (row.func_area_code === 'Defa' || row.func_area_code === 'DEFA' ? 'DEFA' : row.func_area_code === 'Free' || row.func_area_code === 'FREE' ? 'FREE' : null);
      return bt === 'DEFA' || bt === 'Defa' ? 'Godrej' : bt === 'FREE' || bt === 'Free' ? 'Freelancer' : bt ?? "";
    } },
    { header: "Title", key: "title", accessor: (row) => row.title ?? "", editable: true },
    { header: "Branch", accessor: (row) => row.branch ?? "" },
    { header: "Functional Area", accessor: (row) => funcAreaMap[String(row.func_sub_area_code)] ? `${funcAreaMap[String(row.func_sub_area_code)]} [${row.func_sub_area_code}]` : row.func_sub_area_code ?? "" },
    { header: "Training Course", accessor: (row) => trngCourseMap[String(row.trng_course_code)] ? `${trngCourseMap[String(row.trng_course_code)]} [${row.trng_course_code}]` : row.trng_course_code ?? "" },
    { header: "Faculty", accessor: (row) => [row.faculty_code, row.faculty_code_1, row.faculty_code_2, row.faculty_code_3, row.faculty_code_4].filter(Boolean).map(c => facultyMap[String(c)] || String(c)).join(', ') },
    { header: "Start Date", accessor: (row) => { if (!row.start_date) return ""; const [y,m,d] = row.start_date.split('T')[0].split('-'); return `${d}/${m}/${y}`; } },
    { header: "End Date", accessor: (row) => { if (!row.end_date) return ""; const [y,m,d] = row.end_date.split('T')[0].split('-'); return `${d}/${m}/${y}`; } },
    { header: "Start Time", key: "start_time", accessor: (row) => row.start_time ?? "", editable: true },
    { header: "End Time", key: "end_time", accessor: (row) => row.end_time ?? "", editable: true },
    { header: "No. of Hrs", accessor: (row) => {
      if (row.no_of_hrs != null && row.no_of_hrs !== '') return row.no_of_hrs;
      if (!row.start_time || !row.end_time || !row.start_date || !row.end_date) return '';
      const parseT = (t) => { const [h,m] = (t||'').replace('.',':').split(':').map(Number); return isNaN(h)||isNaN(m)?null:h+m/60; };
      const st = parseT(row.start_time), et = parseT(row.end_time);
      if (st===null||et===null||et<=st) return '';
      const days = Math.round((new Date(row.end_date)-new Date(row.start_date))/(864e5))+1;
      return String(Math.round(((et-st)*days)*100)/100);
    } },
    { header: "Venue", key: "venue", accessor: (row) => row.venue ?? "", editable: true },
    { header: "Location", key: "trng_location", accessor: (row) => row.trng_location ?? "", editable: true },
    { header: "City", key: "trng_city", accessor: (row) => row.trng_city ?? "", editable: true },
    { header: "Batch Size", key: "batch_size", accessor: (row) => row.batch_size ?? "", editable: true },
    {
      header: "Status",
      key: "trng_status",
      accessor: (row) => row.trng_status ?? "",
      editable: true,
      renderEdit: (idx, eRows, onChange) => (
        <select className="dt-edit-input" value={eRows[idx]?.trng_status ?? ''} onChange={(e) => onChange(idx, 'trng_status', e.target.value)}>
          <option value="FORMED">FORMED</option>
          <option value="SCHEDULED">SCHEDULED</option>
          <option value="COMPLETED">COMPLETED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>
      ),
    },
  ];

  const calcHrs = (start, end, startTime, endTime) => {
    if (!start || !end) return '';
    const parseTime = (t) => {
      if (!t) return null;
      const [h, m] = t.replace('.', ':').split(':').map(Number);
      return isNaN(h) || isNaN(m) ? null : h + m / 60;
    };
    const st = parseTime(startTime);
    const et = parseTime(endTime);
    if (st === null || et === null || et <= st) return '';
    const dailyHrs = et - st;
    const startDay = new Date(new Date(start).setHours(0, 0, 0, 0));
    const endDay = new Date(new Date(end).setHours(0, 0, 0, 0));
    const numDays = Math.round((endDay - startDay) / (1000 * 60 * 60 * 24)) + 1;
    const total = dailyHrs * numDays;
    return String(Math.round(total * 100) / 100);
  };

  const setField = (key, val) => setForm((prev) => {
    const updated = { ...prev, [key]: val };
    if (['start_date', 'end_date', 'start_time', 'end_time'].includes(key)) {
      const start = key === 'start_date' ? val : prev.start_date;
      const end = key === 'end_date' ? val : prev.end_date;
      const startTime = key === 'start_time' ? val : prev.start_time;
      const endTime = key === 'end_time' ? val : prev.end_time;
      updated.no_of_hrs = calcHrs(start, end, startTime, endTime);
    }
    return updated;
  });

  const handleFuncAreaSelect = (row) => {
    setForm((prev) => ({
      ...prev,
      func_area_code: row.func_area_code ?? "",
      func_sub_area_code: row.func_sub_area_code ?? "",
      func_area_display: row.func_sub_area_name ?? "",
    }));
    setShowFuncAreaViewer(false);
  };

  const handleTrngCourseSelect = (row) => {
    setForm((prev) => ({
      ...prev,
      trng_course_code: row.trng_course_code ?? "",
      trng_course_display: row.trng_course_name ?? "",

    }));
    setShowTrngCourseViewer(false);
  };

  const handleFacultySelect = (row) => {
    if (form.facultyList.length >= 5) {
      alert('Max no. of faculties for a training are assigned already');
      setShowFacultyViewer(false);
      return;
    }
    setForm((prev) => ({ ...prev, facultyList: [...prev.facultyList, row] }));
    setShowFacultyViewer(false);
  };

  const handleBatchSelect = (row) => { setField("batch_sr_no", row.batch_sr_no ?? ""); setShowBatchViewer(false); };

  const handleUpdate = () => {
    setShowEditCols(true);
    setShowDeleteCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const handleDelete = () => {
    setShowDeleteCols(true);
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const toggleEditRow = (idx, row) => {
    setEditRows(prev => {
      if (prev[idx]) { const n = { ...prev }; delete n[idx]; return n; }
      return { ...prev, [idx]: {
        title: row.title ?? '',
        venue: row.venue ?? '',
        trng_location: row.trng_location ?? '',
        trng_city: row.trng_city ?? '',
        start_time: row.start_time ?? '',
        end_time: row.end_time ?? '',
        batch_size: row.batch_size ? String(row.batch_size) : '',
        trng_status: row.trng_status ?? '',
      }};
    });
    setSelectedForUpdate(prev => {
      if (prev[idx]) { const n = { ...prev }; delete n[idx]; return n; }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete(prev => {
      if (prev[idx]) { const n = { ...prev }; delete n[idx]; return n; }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    const idxs = Object.keys(selectedForUpdate).filter(i => selectedForUpdate[i]);
    if (!idxs.length) { setUpdateMsg({ text: 'No records selected for update.', success: false }); return; }
    let ok = 0, fail = 0;
    for (const idx of idxs) {
      const row = tableRows[idx];
      const edited = editRows[idx];
      if (!edited) continue;
      try {
        const res = await batchStatusAPI.update(row.batch_sr_no, {
          branch: row.branch,
          title: edited.title,
          venue: edited.venue,
          trng_location: edited.trng_location,
          trng_city: edited.trng_city,
          start_time: edited.start_time,
          end_time: edited.end_time,
          batch_size: edited.batch_size ? Number(edited.batch_size) : row.batch_size,
          trng_status: edited.trng_status,
        });
        if (res.ok) ok++; else fail++;
      } catch { fail++; }
    }
    if (ok) setUpdateMsg({ text: `${ok} record(s) updated successfully.`, success: true });
    if (fail) setUpdateMsg({ text: `${fail} record(s) failed to update.`, success: false });
    setSelectedForUpdate({}); setEditRows({});
    if (ok) await handleView();
  };

  const handleDeleteSelected = async () => {
    const idxs = Object.keys(selectedForDelete).filter(i => selectedForDelete[i]);
    if (!idxs.length) { setDeleteMsg({ text: 'No records selected for delete.', success: false }); return; }
    if (!window.confirm(`Delete ${idxs.length} batch(es)? This cannot be undone.`)) return;
    let ok = 0, fail = 0;
    for (const idx of idxs) {
      const row = tableRows[idx];
      try {
        const res = await batchStatusAPI.delete(row.batch_sr_no, row.branch);
        if (res.ok) ok++; else fail++;
      } catch { fail++; }
    }
    if (ok) setDeleteMsg({ text: `${ok} record(s) deleted successfully.`, success: true });
    if (fail) setDeleteMsg({ text: `${fail} record(s) failed.`, success: false });
    setSelectedForDelete({});
    if (ok) await handleView();
  };

  const handleSave = async () => {
    if (!form.func_sub_area_code) { alert('Functional Area is required'); return; }
    if (!form.trng_course_code) { alert('Training Course is required'); return; }
    if (!form.start_date) { alert('Start Date is required'); return; }
    if (!form.end_date) { alert('End Date is required'); return; }
    if (!form.start_time) { alert('Start Time is required'); return; }
    const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!timeRegex.test(form.start_time)) { alert('Start Time must be in 24-hour format HH:MM (e.g. 09:30)'); return; }
    if (form.end_time && !timeRegex.test(form.end_time)) { alert('End Time must be in 24-hour format HH:MM (e.g. 17:00)'); return; }
    if (!form.batch_size) { alert('Batch Size is required'); return; }
    if (!form.venue.trim()) { alert('Venue is required'); return; }
    const fc = form.facultyList;
    const payload = {
      batch_type: form.batch_type,
      branch: form.branch || undefined,
      func_area_code: form.batch_type === "DEFA" ? "Defa" : "Free",
      func_sub_area_code: form.func_sub_area_code,
      trng_course_code: form.trng_course_code ? Number(form.trng_course_code) : undefined,
      title: form.title || undefined,
      start_date: form.start_date ? `${form.start_date.getFullYear()}-${String(form.start_date.getMonth()+1).padStart(2,'0')}-${String(form.start_date.getDate()).padStart(2,'0')}` : "",
      end_date: form.end_date ? `${form.end_date.getFullYear()}-${String(form.end_date.getMonth()+1).padStart(2,'0')}-${String(form.end_date.getDate()).padStart(2,'0')}` : "",
      start_time: form.start_time ? form.start_time.replace('.', ':') : "",
      end_time: form.end_time ? form.end_time.replace('.', ':') : undefined,
      no_of_hrs: form.no_of_hrs ? Number(form.no_of_hrs) : undefined,
      batch_size: form.batch_size ? Number(form.batch_size) : undefined,
      ...(fc[0]?.faculty_code ? { faculty_code: Number(fc[0].faculty_code) } : {}),
      ...(fc[1]?.faculty_code ? { faculty_code_1: Number(fc[1].faculty_code) } : {}),
      ...(fc[2]?.faculty_code ? { faculty_code_2: Number(fc[2].faculty_code) } : {}),
      ...(fc[3]?.faculty_code ? { faculty_code_3: Number(fc[3].faculty_code) } : {}),
      ...(fc[4]?.faculty_code ? { faculty_code_4: Number(fc[4].faculty_code) } : {}),
      ...(fc.length > 0 ? { number_of_faculty: fc.length } : {}),
      professional_fees: form.professional_fees !== "" ? Number(form.professional_fees) : undefined,
      trng_rel_cost: form.trng_rel_cost !== "" ? Number(form.trng_rel_cost) : undefined,
      trng_rel_cost_rmk: form.trng_rel_cost_rmk || undefined,
      venue: form.venue || undefined,
      trng_location: form.trng_location || undefined,
      trng_city: form.trng_city || undefined,
      division: form.division || undefined,
      trng_status: "FORMED",
      emp_cost: form.emp_cost !== "" ? Number(form.emp_cost) : undefined,
      total_cost: form.total_cost !== "" ? Number(form.total_cost) : undefined,
      per_emp_per_day_fc: form.per_emp_per_day_fc !== "" ? Number(form.per_emp_per_day_fc) : undefined,
      per_emp_per_day_mc: form.per_emp_per_day_mc !== "" ? Number(form.per_emp_per_day_mc) : undefined,
      per_emp_per_day_cost: form.per_emp_per_day_cost !== "" ? Number(form.per_emp_per_day_cost) : undefined,
      reason: form.reason || undefined,
      dph_sal_code: form.dph_sal_code || undefined,
      trng_type: "Internal",
      number_attended: form.number_attended ? Number(form.number_attended) : undefined,
    };
    try {
      const res = await batchStatusAPI.create(payload);
      if (res.status === 409) {
        const err = await res.json();
        alert(err.message || 'A batch already exists with overlapping dates and times for this branch.');
        return;
      }
      if (res.ok) {
        const saved = await res.json();
        const item = saved.item || {};
        // Send batch formation notification email
        const trainingData = {
          batchSrNo: item.batch_sr_no || payload.batch_sr_no,
          funcSubAreaName: form.func_area_display,
          trngCourseName: form.trng_course_display,
          startDate: payload.start_date,
          endDate: payload.end_date,
          startTime: payload.start_time,
          endTime: payload.end_time,
          venue: payload.venue,
          trngLocation: payload.trng_location,
          trngCity: payload.trng_city,
          batchSize: payload.batch_size,
          faculty: form.facultyList.map(f => f.faculty_name).join(', '),
        };

        // Send faculty notification emails
        await Promise.all(
          form.facultyList
            .filter(f => f.email)
            .map(f =>
              emailAPI.notifyFaculty({
                facultyData: { emailId: f.email, name: f.faculty_name ?? '' },
                trainingData,
              }).catch(() => {})
            )
        );
        alert("Training batch saved successfully");
        setForm(INIT_FORM);
        setShowInsertForm(false);
        // Bust the course cache so the new course name appears in the table
        invalidateTrngCourses();
        // Rebuild all maps so the saved row shows names correctly
        const [freshCourses, freshFuncAreas, freshFaculties] = await Promise.all([
          getTrngCourses(),
          getFuncAreas(),
          facultyAPI.getAll(),
        ]);
        const freshTMap = {};
        (Array.isArray(freshCourses) ? freshCourses : []).forEach(c => {
          freshTMap[String(c.trng_course_code)] = c.trng_course_name;
        });
        setTrngCourseMap(freshTMap);
        const freshFMap = {};
        (Array.isArray(freshFuncAreas) ? freshFuncAreas : []).forEach(f => {
          freshFMap[String(f.func_sub_area_code)] = f.func_sub_area_name;
        });
        setFuncAreaMap(freshFMap);
        const freshFacMap = {};
        (Array.isArray(freshFaculties) ? freshFaculties : []).forEach(f => {
          freshFacMap[String(f.faculty_code)] = f.faculty_name;
        });
        setFacultyMap(freshFacMap);
        // Show only the newly inserted record in the table
        setTableRows([item]);
        setHasMore(false);
        lastKeyRef.current = null;
        setShowTable(true);
      } else {
        const err = await res.json();
        alert(err.message || "Failed to save");
      }
    } catch (e) {
      alert("Error saving training batch");
    }
  };

  return (
    <div className="bd-container">
      {/* ── Viewer Modals ── */}
      {showFuncAreaViewer && (
        <div className="sp-modal-overlay" onClick={() => setShowFuncAreaViewer(false)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sp-modal-close" onClick={() => setShowFuncAreaViewer(false)}>&times;</button>
            <FuncAreaViewer selectable onSelect={handleFuncAreaSelect} autoLoad />
          </div>
        </div>
      )}
      {showTrngCourseViewer && (
        <div className="sp-modal-overlay" onClick={() => setShowTrngCourseViewer(false)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sp-modal-close" onClick={() => setShowTrngCourseViewer(false)}>&times;</button>
            <TrngCourseViewer
              selectable
              onSelect={handleTrngCourseSelect}
              autoLoad
              funcSubAreaCode={form.func_sub_area_code}
              funcAreaCode={form.batch_type === "FREE" ? "Free" : "Defa"}
            />
          </div>
        </div>
      )}
      {showFacultyViewer && (
        <div className="sp-modal-overlay" onClick={() => setShowFacultyViewer(false)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sp-modal-close" onClick={() => setShowFacultyViewer(false)}>&times;</button>
            <FacultyViewer selectable onSelect={handleFacultySelect} autoLoad alreadySelected={form.facultyList} funcSubAreaCode={form.func_sub_area_code} />
          </div>
        </div>
      )}
      {showBatchViewer && (
        <div className="sp-modal-overlay" onClick={() => setShowBatchViewer(false)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sp-modal-close" onClick={() => setShowBatchViewer(false)}>&times;</button>
            <BatchViewer selectable onSelect={handleBatchSelect} autoLoad />
          </div>
        </div>
      )}
      {/* ── Filter form ── */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <table className="sp-filter-table">
          <tbody>
            <tr>
              <td>Schedule for Branch</td>
              <td>
                <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
                  <option value=""></option>
                  {branches.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </td>
              <td>Financial Year</td>
              <td>
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                  <option value=""></option>
                  <option>2005-06</option>
                  <option>2006-07</option>
                  <option>2007-08</option>
                  <option>2008-09</option>
                  <option>2009-10</option>
                  <option>2010-11</option>
                  <option>2011-12</option>
                  <option>2012-13</option>
                  <option>2013-14</option>
                  <option>2014-15</option>
                  <option>2015-16</option>
                  <option>2016-17</option>
                  <option>2017-18</option>
                  <option>2018-19</option>
                  <option>2019-20</option>
                  <option>2020-21</option>
                  <option>2021-22</option>
                  <option>2022-23</option>
                  <option>2023-24</option>
                  <option>2024-25</option>
                  <option>2025-26</option>
                  <option>2026-27</option>
                </select>
              </td>
              <td>Month</td>
              <td>
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                  <option value=""></option>
                  <option>April</option>
                  <option>May</option>
                  <option>June</option>
                  <option>July</option>
                  <option>August</option>
                  <option>September</option>
                  <option>October</option>
                  <option>November</option>
                  <option>December</option>
                  <option>January</option>
                  <option>February</option>
                  <option>March</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="sp-btn-row">
        <button
          className="sp-btn sp-btn-outline"
          onClick={() => { setShowInsertForm(true); setShowTable(false); setForm((prev) => ({ ...INIT_FORM, branch: selectedBranch || '' })); }}
        >
          Insert
        </button>
        <button
          className="sp-btn sp-btn-outline"
          onClick={() => { setShowInsertForm(false); handleView(); }}
        >
          View
        </button>
        <button
          className="sp-btn sp-btn-outline"
          onClick={async () => { setShowInsertForm(false); await handleView(); handleUpdate(); }}
        >
          Update
        </button>
        <button
          className="sp-btn sp-btn-outline"
          onClick={async () => { setShowInsertForm(false); await handleView(); handleDelete(); }}
        >
          Delete
        </button>
        <button
          className="sp-btn sp-btn-outline"
          onClick={() => { setShowTable(false); setShowInsertForm(false); setSelectedBranch(''); setSelectedYear(''); setSelectedMonth(''); setHasMore(false); setTableRows([]); lastKeyRef.current = null; setShowEditCols(false); setShowDeleteCols(false); setEditRows({}); setSelectedForUpdate({}); setSelectedForDelete({}); setUpdateMsg(null); setDeleteMsg(null); }}
        >
          Reset
        </button>
      </div>

      {/* ── Insert Form ── */}
      {showInsertForm && (
        <div className="sp-insert-container">

          <table className="sp-form-table">
            <tbody>
              <tr>
                <td className="sp-td-label">Batch Type</td>
                <td className="sp-td-field">
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 30 }}>
                    <input type="radio" name="batchType" checked={form.batch_type === "DEFA"} onChange={() => setForm(p => ({ ...p, batch_type: "DEFA", trng_course_code: "", trng_course_display: "" }))} /> Godrej Training Batch
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <input type="radio" name="batchType" checked={form.batch_type === "FREE"} onChange={() => setForm(p => ({ ...p, batch_type: "FREE", trng_course_code: "", trng_course_display: "" }))} /> Freelance Training Batch
                  </label>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="sp-two-col">
            <table className="sp-form-table">
              <tbody>
                <tr>
                  <td className="sp-td-label">Branch</td>
                  <td className="sp-td-field">
                    {selectedBranch
                      ? <input type="text" value={form.branch} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                      : <select value={form.branch} onChange={(e) => setField("branch", e.target.value)}>
                          <option value="">--Select--</option>
                          {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                    }
                  </td>
                </tr>
                <tr>
                  <td className="sp-td-label">Functional Area *</td>
                  <td className="sp-td-field">
                    <input type="text" value={form.func_area_display} readOnly />
                    <button className="sp-btn" onClick={() => setShowFuncAreaViewer(true)}>Get</button>
                    <button className="sp-btn-clear" onClick={() => setForm((p) => ({ ...p, func_area_code: "", func_sub_area_code: "", func_area_display: "" }))}>Clear</button>
                  </td>
                </tr>
                <tr>
                  <td className="sp-td-label">Training Course *</td>
                  <td className="sp-td-field">
                    <input type="text" value={form.trng_course_display} readOnly />
                    <button className="sp-btn" onClick={() => { if (!form.func_sub_area_code) { alert('Please select a Functional Area first.'); return; } setShowTrngCourseViewer(true); }}>Get</button>
                    <button className="sp-btn-clear" onClick={() => setForm((p) => ({ ...p, trng_course_code: "", trng_course_display: "" }))}>Clear</button>
                  </td>
                </tr>
                <tr>
                  <td className="sp-td-label">Title</td>
                  <td className="sp-td-field"><input type="text" value={form.title} onChange={(e) => setField("title", e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">Batch Sr. No.</td>
                  <td className="sp-td-field"><input type="text" value={form.batch_sr_no} readOnly placeholder="Auto-generated" /></td>
                </tr>
                <tr>
                  <td className="sp-td-label" style={{ verticalAlign: 'top', paddingTop: 8 }}>Faculty *</td>
                  <td className="sp-td-field" style={{ verticalAlign: 'top', paddingTop: 8 }}>
                    <textarea rows="3" value={form.facultyList.map((f) => f.faculty_name ?? "").join(", ")} readOnly />
                    <button className="sp-btn" onClick={() => setShowFacultyViewer(true)} disabled={form.facultyList.length >= 5}>Get Faculty</button>
                    <button className="sp-btn-clear" onClick={() => setField("facultyList", [])}>Clear</button>
                  </td>
                </tr>
                <tr>
                  <td className="sp-td-label">Venue *</td>
                  <td className="sp-td-field"><input type="text" maxLength="40" value={form.venue} onChange={(e) => setField("venue", e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">Location</td>
                  <td className="sp-td-field"><input type="text" maxLength="255" value={form.trng_location} onChange={(e) => setField("trng_location", e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">City</td>
                  <td className="sp-td-field"><input type="text" maxLength="255" value={form.trng_city} onChange={(e) => setField("trng_city", e.target.value)} /></td>
                </tr>
              </tbody>
            </table>

            <table className="sp-form-table">
              <tbody>
                <tr>
                  <td className="sp-td-label">Start Date * (DD/MM/YY)</td>
                  <td className="sp-td-field">
                    <div className="sp-date-wrap" onClick={() => setStartDateOpen(true)}>
                      <DatePicker
                        open={startDateOpen}
                        onClickOutside={() => setStartDateOpen(false)}
                        selected={form.start_date}
                        onChange={(date) => { setField("start_date", date); setStartDateOpen(false); }}
                        dateFormat="dd/MM/yyyy"
                        className="sp-date-input"
                        placeholderText="Select start date"
                        showIcon
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        customInput={<input readOnly />}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="sp-td-label">End Date * (DD/MM/YY)</td>
                  <td className="sp-td-field">
                    <div className="sp-date-wrap" onClick={() => {
                      if (!form.start_date) { alert('Please select a Start Date first.'); return; }
                      setEndDateOpen(true);
                    }}>
                      <DatePicker
                        open={endDateOpen}
                        onClickOutside={() => setEndDateOpen(false)}
                        selected={form.end_date}
                        onChange={(date) => { setField("end_date", date); setEndDateOpen(false); }}
                        dateFormat="dd/MM/yyyy"
                        className="sp-date-input"
                        placeholderText="Select end date"
                        minDate={form.start_date}
                        showIcon
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        customInput={<input readOnly />}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="sp-td-label">Start Time * (HH:MM)</td>
                  <td className="sp-td-field"><input type="text" maxLength="5" value={form.start_time} onChange={(e) => setField("start_time", e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">End Time (HH:MM)</td>
                  <td className="sp-td-field"><input type="text" maxLength="5" value={form.end_time} onChange={(e) => setField("end_time", e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">No. of Hrs *</td>
                  <td className="sp-td-field"><input type="text" maxLength="6" value={form.no_of_hrs} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">Batch Size *</td>
                  <td className="sp-td-field"><input type="text" maxLength="3" value={form.batch_size} onChange={(e) => setField("batch_size", e.target.value)} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">Total Faculty Cost (Rs)<br /><span className="sp-remark-hint">[Total]</span></td>
                  <td className="sp-td-field"><input type="text" maxLength="8" value={form.professional_fees} onChange={(e) => { if (/^\d*(\.\d*)?$/.test(e.target.value)) setField("professional_fees", e.target.value); }} onBlur={(e) => { const n = Number(e.target.value); if (e.target.value !== '' && !isNaN(n) && e.target.value !== '.') setField("professional_fees", String(Math.round(n))); else if (e.target.value === '.') setField("professional_fees", ''); }} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">Total Venue Cost (Rs)<br /><span className="sp-remark-hint">[Write details in Remark]</span></td>
                  <td className="sp-td-field"><input type="text" maxLength="8" value={form.trng_rel_cost} onChange={(e) => { if (/^\d*(\.\d*)?$/.test(e.target.value)) setField("trng_rel_cost", e.target.value); }} onBlur={(e) => { const n = Number(e.target.value); if (e.target.value !== '' && !isNaN(n) && e.target.value !== '.') setField("trng_rel_cost", String(Math.round(n))); else if (e.target.value === '.') setField("trng_rel_cost", ''); }} /></td>
                </tr>
                <tr>
                  <td className="sp-td-label">Training/Venue Cost Remark<br /><span className="sp-remark-hint">[Venue, Study Material, etc.]</span></td>
                  <td className="sp-td-field"><input type="text" value={form.trng_rel_cost_rmk} onChange={(e) => setField("trng_rel_cost_rmk", e.target.value)} /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sp-form-actions">
            <button className="sp-btn sp-btn-outline" onClick={handleSave}>Save</button>
            <button className="sp-btn sp-btn-outline" onClick={() => setForm(INIT_FORM)}>Reset</button>
          </div>
        </div>
      )}

      {/* ── Table View ── */}
      {updateMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: 'GEG-Body', color: updateMsg.success ? 'green' : 'red', margin: '4px 0' }}>{updateMsg.text}</p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: 'GEG-Body', color: deleteMsg.success ? 'green' : 'red', margin: '4px 0' }}>{deleteMsg.text}</p>
      )}
      {tableLoading && <p style={{ fontFamily: 'GEG-Body', fontSize: 12.5, padding: 16 }}>Loading...</p>}
      {showTable && (
        // <>
        <div className="bd-details">
          <DataTable
            columns={scheduleColumns}
            data={tableRows}
            showEditCols={showEditCols}
            showDeleteCols={showDeleteCols}
            separateCols={true}
            editRows={editRows}
            onToggleEditRow={toggleEditRow}
            onChangeEditCell={(idx, key, value) => setEditRows(prev => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))}
            selectedForDelete={selectedForDelete}
            onToggleDeleteRow={toggleDeleteRow}
            emptyMessage="No schedule data found"
            rowKey={(row, i) => `${row.batch_sr_no}-${i}`}
          />

          <div style={{ display: 'flex', justifyContent: 'center', margin: '12px auto', fontSize: 11, fontFamily: 'GEG-Body', width: 'calc(100% - 40px)' }}>
            {hasMore && (
              <button
                className="btn btn-outline"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            )}
            {!hasMore && tableRows.length > 0 && (
              <span style={{ color: '#888' }}>All {tableRows.length} record(s) loaded.</span>
            )}
          </div>
          {showEditCols && (
            <UpdateComponent
              onUpdateSelected={handleUpdateSelected}
              onDeleteSelected={null}
            />
          )}
          {showDeleteCols && (
            <UpdateComponent
              onUpdateSelected={null}
              onDeleteSelected={handleDeleteSelected}
            />
          )}
        </div>



        // </> 
      )}
    </div>
  );
}
